import { EditableContent } from '../../edit/EditableContent';
import { EditableMedia } from '../../edit/EditableMedia';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../../lib/responsiveImageSizes';
import { pickBlockField } from '../../lib/localized';
import { pickMediaUrl } from '../../lib/media';
import { readBlockCtaIntent, resolveBlockCtaHref } from '../../lib/resolveSectionHref';
import type { BlockRenderProps } from '../../types';

/**
 * Counter Bleed — full-bleed counter photography with a restrained copy stack.
 * Optional `sealImage` remix only — default seed omits the mark above the
 * eyebrow (glyph lives in nav + Footer Colophon). Not Quiet Luxury / Full-Bleed.
 */
export function HeroCounterBleed({
  block,
  locale,
  defaultLocale = locale,
  isActive,
  activeEditTarget,
  editable,
  headingLevel = 'h1',
  pages,
  siteMode,
  allBlocks,
}: BlockRenderProps) {
  const Heading = headingLevel;
  const eyebrow = pickBlockField(block.content, 'eyebrow', locale);
  const heading = pickBlockField(block.content, 'heading', locale);
  const subheading = pickBlockField(block.content, 'subheading', locale);
  const ctaPrimary = pickBlockField(block.content, 'ctaPrimary', locale);
  const ctaPrimaryIntent = readBlockCtaIntent(block.content, locale, {
    hrefFieldKey: 'ctaPrimaryHref',
    fallback: 'book',
  });
  const ctaPrimaryHref = resolveBlockCtaHref(ctaPrimaryIntent, {
    locale,
    defaultLocale,
    content: block.content,
    hrefFieldKey: 'ctaPrimaryHref',
    siteMode,
    pages,
    allBlocks,
  });
  const heroImage = pickMediaUrl(block, 'heroImage');
  const heroImageSrc = heroImage ? buildSiteImageProxyUrl(heroImage, 1800, 78) : undefined;
  const heroImageSrcSet = heroImage
    ? buildSiteImageSrcSet(heroImage, [480, 768, 1200, 1600, 2000], 78)
    : undefined;
  const sealImage = pickMediaUrl(block, 'sealImage');
  const sealSrc = sealImage ? buildSiteImageProxyUrl(sealImage, 240, 78) : undefined;
  const sealSrcSet = sealImage ? buildSiteImageSrcSet(sealImage, [120, 180, 240], 78) : undefined;
  const imageAlt = heading || eyebrow || 'Hero';
  const target = isActive ? activeEditTarget : null;

  return (
    <section
      className={['ws-hero-counter', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      id="top"
    >
      <div className="ws-hero-counter__stage" aria-hidden={!heroImage}>
        {heroImage ? (
          <EditableMedia
            fieldKey="heroImage"
            editable={editable}
            activeEditTarget={target}
            className="ws-hero-counter__photo"
            src={heroImageSrc}
            srcSet={heroImageSrcSet}
            sizes={SITE_IMAGE_SIZES.heroFullBleed}
            alt={imageAlt}
            width={1600}
            height={900}
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        ) : (
          <div className="ws-hero-counter__placeholder" aria-hidden />
        )}
        <div className="ws-hero-counter__veil" aria-hidden />
      </div>
      <div id="about" className="ws-hero-counter__copy">
        <div className="ws-hero-counter__pocket">
          {sealSrc ? (
            <div className="ws-hero-counter__seal">
              <EditableMedia
                fieldKey="sealImage"
                editable={editable}
                activeEditTarget={target}
                className="ws-hero-counter__seal-img"
                src={sealSrc}
                srcSet={sealSrcSet}
                sizes={SITE_IMAGE_SIZES.heroMonolithSeal}
                alt={heading || 'Mark'}
                width={120}
                height={120}
                loading="eager"
                decoding="async"
              />
            </div>
          ) : null}
          <EditableContent
            fieldKey="eyebrow"
            editable={editable}
            activeEditTarget={target}
            className="ws-hero-counter__eyebrow"
          >
            {eyebrow}
          </EditableContent>
          <EditableContent
            as={Heading}
            fieldKey="heading"
            editable={editable}
            activeEditTarget={target}
            className="ws-hero-counter__title"
          >
            {heading}
          </EditableContent>
          <EditableContent
            fieldKey="subheading"
            editable={editable}
            activeEditTarget={target}
            className="ws-hero-counter__lead"
          >
            {subheading}
          </EditableContent>
          {ctaPrimary ? (
            <EditableContent
              as="a"
              fieldKey="ctaPrimary"
              editable={editable}
              activeEditTarget={target}
              className="ws-hero-counter__link"
              href={ctaPrimaryHref}
            >
              {ctaPrimary}
            </EditableContent>
          ) : null}
        </div>
      </div>
    </section>
  );
}
